import React, {
  ForwardRefRenderFunction,
  RefObject,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { AxiosError } from "axios";
import { useTranslation } from "react-i18next";

import { NotificationToast } from "../../../../../../../../components/Generic/Toast/NotificationToast";
import UploadCSVFile, {
  UploadCSVErrors,
} from "../../../../../../../../components/Generic/UploadCSVFile";
import { ValidateCSVFieldErrors } from "../../../../../../../../components/Generic/UploadCSVFile/types";
import { useAppState } from "../../../../../../../../context/StateContext";
import { CommodityCodeTemplateCSV } from "../../../../../../../../models/commodity.code";
import { Status } from "../../../../../../../../util/enums";
import readCSV from "../../../../../../../../util/readCSV";
import useActions from "../../hooks/useActions";
import { CodeType, CodeTypeNumber } from "../../utils";
import * as ValidateCSV from "./csv.validate";
import useStyles from "./styles";

export interface ImportHSCodeModalHandles {
  open: () => void;
  close: () => void;
}

interface Props {
  ref: RefObject<ImportHSCodeModalHandles>;
}

const ImportHSCodeModal: ForwardRefRenderFunction<ImportHSCodeModalHandles, Props> = (
  props,
  ref
) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const modalRef = useRef<ImportHSCodeModalHandles>(null);

  const closeModal = useCallback(() => modalRef.current?.close(), []);
  const openModal = useCallback(() => modalRef.current?.open(), []);

  useImperativeHandle(ref, () => ({ close: closeModal, open: openModal }));

  const [lineErrorsCSV, setLineErrorsCSV] = useState<UploadCSVErrors>({});
  const [generalErrorsCSV, setGeneralErrorsCSV] = useState<UploadCSVErrors>({});

  const { useCreateCommodityCodes, variables } = useActions();
  const { mutate: createCommodityCodes, isLoading: isLoadingCreating } = useCreateCommodityCodes();
  const { hscodes, isFetchingHSCodes, regions } = variables;

  const onConfirmUploadCSV = async (data: CommodityCodeTemplateCSV[]) => {
    if (!data?.length) {
      const message = translate("thereAreNoRecordsInThisFile");
      const option = { id: "warning-file-empty" };

      NotificationToast.warning(message, option);

      return;
    }

    const lineErrors: ValidateCSVFieldErrors = {};
    const csvGlobalErrors: UploadCSVErrors = {};

    const { valid } = ValidateCSV.validateCSVFields(data, lineErrors, translate, hscodes, regions);

    const commodityCodes = data.map(({ code, regions: csvRegions, statisticalClassification }) => {
      const parent = code.substring(0, 6);
      const regionNames = csvRegions.split(",").map((region) => region.trim());
      const hscode = hscodes?.find(({ hscode }) => hscode === parent);

      return {
        chapterId: hscode?.chapterId,
        companyId: currentUser?.companyId,
        description: hscode?.description,
        hscode: code,
        level: CodeTypeNumber[CodeType.COMMODITY_CODE],
        parent,
        regions: regions?.filter(({ name }) => regionNames.includes(name)),
        sectionId: hscode?.sectionId,
        statisticalClassification,
        status: Status.ACTIVE,
      };
    });

    if (!valid || !!Object.keys(csvGlobalErrors).length) {
      setLineErrorsCSV(lineErrors);
      setGeneralErrorsCSV(csvGlobalErrors);

      return;
    }

    createCommodityCodes(commodityCodes, {
      onError: (error) => {
        const errorAxios = error as AxiosError<Error>;

        modalRef?.current?.close();
        NotificationToast.error(
          errorAxios.response?.data?.message || translate("anErrorOccurredTryAgain")
        );
      },
      onSuccess: (_response) => {
        modalRef?.current?.close();
      },
    });
  };

  const error = Object.keys({ ...generalErrorsCSV, ...lineErrorsCSV }).length > 0;

  return (
    <UploadCSVFile
      className={classes.uploadModal}
      generalErrors={generalErrorsCSV}
      lineErrors={lineErrorsCSV}
      loadingUpload={isLoadingCreating || isFetchingHSCodes}
      maxSize={2}
      modalTitle={error ? translate("errorsFound") : translate("importCSVFile")}
      onCloseModal={() => {
        setLineErrorsCSV({});
        setGeneralErrorsCSV({});
      }}
      onConfirmUpload={(uri) => {
        readCSV<CommodityCodeTemplateCSV>(
          uri,
          ["code", "statisticalClassification", "regions"],
          onConfirmUploadCSV
        );
      }}
      ref={modalRef}
      textSave={translate("import")}
    />
  );
};

export default forwardRef(ImportHSCodeModal);
